import { Component, OnInit } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { CsvExportCompoundDialogComponent } from '@client/app/modules/export/components/dialog/dialog-compound.component';
import moment from 'moment';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { CrawlsRepoService } from '@client/app/services/api/crawls-repo/crawls-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { ParamService } from '@monsido/core/param/param.service';
import { StateService } from '@uirouter/core';
import { ExporterService } from '@client/app/modules/export/services/exporter.service';
import {
    ChangesHistoryService,
} from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import { SessionService } from '@monsido/core/session/session.service';
import { DEFAULTS } from '@monsido/core/constants/defaults.constant';

@Component({
    selector: 'changes-history',
    standalone: false,
    template: `
        <div class="changes-history">
            @let headerText = 'Scan History' | translate;
            @let headerHTML = '<div class="pull-left">' + headerText + '</div>';
            <page-header
                    [icon]="['CHANGES_HISTORY', 'SUMMARY'] | monIcons"
                    [header]="headerHTML"
                    [subHeader]="'Track changes across domains and modules' | translate"
                    panelRightClass="text-right">
                <page-header-content>
                    <button type="button"
                            *ngIf="showExport"
                            class="btn btn-border-radius btn-secondary font-size-1-1"
                            (click)="exportCompoundCsv()"
                            monEventTrigger="actionTrigger"
                            [monEventParams]="{action:'btn-export'}"
                            [attr.aria-label]="'Export Compound CSV' | translate">
                            <span class="inline-block">
                                <span class="fas fa-download fa-lg pr-1" aria-hidden="true"></span>
                                {{ 'Compound Export' | translate }}
                            </span>
                    </button>
                </page-header-content>
            </page-header>
            <hr class="visible-print-block my-0"/>
            <div class="row mt-3 visible-print-block" *ngIf="domain !== null">
                <span class="col-7" translate>Domain:</span>
                <strong class="col-41">{{domain.title}}</strong>
            </div>
            <div class="row visible-print-block" *ngIf="domain !== null">
                <span class="col-7">Module:</span>
                <strong class="col-41">{{moduleTranslation}}</strong>
            </div>
            <div class="row visible-print-block">
                <span class="col-7" translate>Timeframe:</span>
                <strong class="col-41">
                    {{date.startDate.format('ddd, D MMM YYYY')}} to {{date.endDate.format('ddd, D MMM YYYY')}}
                </strong>
            </div>

            <hr class="visible-print-block mb-6"/>
            <div class="row d-flex align-items-center form-horizontal">
                <div class="col-md-18 pr-md-1 hidden-print" *ngIf="domain != null">
                    <form-field-select [options]="modules"
                                       [(ngModel)]="selectedModule"
                                       name="selectedModule"
                                       monEventTrigger="actionTrigger"
                                       [monEventParams]="{action:'history-modules-dropdown'}"
                                       [defaultOption]="'Please select a module' | translate"
                                       nameAttr="name"
                                       (ngChange)="setModule(selectedModule, true)"
                                       dropdownTemplate="app/pages/global/changes-history/dropdown-templates/module-select.html"
                                       viewTemplate="app/pages/global/changes-history/dropdown-templates/module-select.html"
                                       returnValue="value">
                    </form-field-select>
                </div>
                <div class="col-md-12 pl-md-1 hidden-print" *ngIf="showScoresDropdown">
                    <form-field-select [options]="chartTypesOptions"
                                       [(ngModel)]="selectedChart"
                                       name="selectedChart"
                                       nameAttr="name"
                                       (ngChange)="setChartType()"
                                       dropdownTemplate="app/pages/global/changes-history/dropdown-templates/module-select.html"
                                       viewTemplate="app/pages/global/changes-history/dropdown-templates/module-select.html"
                                       returnValue="value">
                    </form-field-select>
                </div>
            </div>

            <div class="card panel-monsido-card hidden-print mb-1 mt-3" *ngIf="domain !== null && selectedModule">
                <div class="card-body py-3 d-flex align-items-center">
                    <div class="py-3 mr-auto">
                        <span class="font-size-1-1 text-grey" translate>Changes in selected time frame:</span>
                        <date-picker
                            *ngIf="!datePickerLoading"
                            [(date)]="date"
                            [monDateFormat]="displayDateFormat"
                            (dateChange)="setDate($event)"
                            [options]="datepickerOptions"
                            class="inline-block"
                        />
                    </div>
                    <div>
                        <button type="button"
                                [ngClass]="{disabled: !selectedModule}"
                                class="btn btn-border-radius btn-link font-size-1-1"
                                [disabled]="!selectedModule"
                                ngCsv
                                [data]="exportCsv()"
                                [quoteStrings]="true"
                                fieldSep=";"
                                filename="history.csv"
                                [attr.aria-label]="'Export CSV' | translate">
                            <span class="fas fa-download fa-lg pr-1" aria-hidden="true"></span>
                            <span translate>Export</span>
                        </button>
                        <button type="button"
                                [ngClass]="{disabled: !selectedModule}"
                                class="btn btn-border-radius btn-link font-size-1-1"
                                [disabled]="!selectedModule"
                                (click)="print()">
                                <span class="fas fa-print fa-lg pr-1" aria-hidden="true"></span>
                                <span translate>Print view</span>
                        </button>
                    </div>
                </div>
            </div>
            <changes-history-dashboard [domain]="domain"
                                       [selectedGroupOption]="selectedGroupOption"
                                       [progress]="progress"
                                       [loading]="loading"
                                       [selectedModule]="selectedModule"
                                       [showScores]="selectedChart === chartTypesOptions[1]?.value"
                                       [domainGroup]="domainGroup">
            </changes-history-dashboard>
        </div>
    `,
})
export class ChangesHistoryComponent implements OnInit {
    showScoresDropdown = false;
    showExport: any;
    loading = false;
    datePickerLoading = false;
    domain: any = null;
    domainGroup: any = null;
    chartTypesOptions: any[] = [];
    selectedChart: any;
    datepickerOptions: any = { opens: 'center' };
    dateFormat = 'YYYY-MM-DD';
    modules: any[] = [];
    date: any = {
        startDate: moment().subtract(3, 'months'),
        endDate: moment(),
    };
    displayDateFormat: any;
    selectedModule = 'all';
    history: any = null;
    moduleTranslation: any;
    settingModule = false;
    selectedGroupOption: any;
    progress: any;

    constructor (
        private activeFeatureService: ActiveFeatureService,
        private crawlsRepoService: CrawlsRepoService,
        private translateService: TranslateService,
        private paramService: ParamService,
        private state: StateService,
        private exporterService: ExporterService,
        private changesHistoryService: ChangesHistoryService,
        private monEventsService: MonEventService,
        private demoHasAccessService: DemoHasAccessService,
        private sessionService: SessionService,
    ) {}

    ngOnInit () {
        this.activate();
        this.initializeDomain();
    }

    initializeDomain () {
        const domain = this.sessionService.domain;
        const domainGroup = this.sessionService.domainGroup;

        if (domain) {
            this.setDomain(domain, domainGroup);
            this.setModule(this.selectedModule);
            this.getCrawlHistory();
        }
    }

    activate () {
        this.settingModule = false;
        this.showExport = this.activeFeatureService.isFeatureActivePure('data_export');
        this.loading = false;
        this.datePickerLoading = false;
        this.domain = null;
        this.domainGroup = null;
        this.chartTypesOptions = [
            {
                name: this.translateService.getString('Issues'),
                value: 'issues',
            },
        ];
        const isDemoAccount = this.demoHasAccessService.isDemoAccount();
        if (!isDemoAccount) {
            this.chartTypesOptions.push({
                name: this.translateService.getString('Scores'),
                value: 'scores',
            });
        }
        this.selectedChart = this.getSelectedChart(this.showScoresDropdown);
        this.datepickerOptions = { opens: 'center' };
        this.dateFormat = 'YYYY-MM-DD';
        this.modules = [];
        this.date = {
            startDate: moment().subtract(3, 'months'),
            endDate: moment(),
        };
        this.displayDateFormat = DEFAULTS.DATE_FORMAT;
        this.selectedModule = 'all';
        this.history = null;
        this.loadLocations();
    }

    exportCsv () {
        return () => {
            if (['seo', 'qa', 'accessibility'].indexOf(this.selectedModule) > -1) {
                return this.exporterService.requestExportFromModule(this.selectedModule);
            }

            if (this.history) {
                return this.exporterService.parseHistory(this.selectedModule, this.history);
            } else {
                return null;
            }
        };
    }

    print () {
        window.print();
    }

    exportCompoundCsv () {
        const dialogParams = {
            component: CsvExportCompoundDialogComponent,
            dialogOptions: {
                size: 'sm',
                classes: 'fade-animation middle center mon-dialog',
            },
            data: {
                exports: [
                    {
                        name: this.translateService.getString('Start compound export'),
                        data: {
                            category: 'scan_history_overview',
                        },
                    },
                ],
            },
        };
        this.monEventsService.run(MON_EVENTS.LOAD_NG2_DIALOG, dialogParams);
    }

    setModule (module: any, shouldSetParams?: any) {
        if (
            typeof module !== 'string' ||
            module === this.changesHistoryService.getModule()
        ) {
            return;
        }

        this.showScoresDropdown = this.getShowScores(this.selectedModule);
        this.selectedChart = this.getSelectedChart(this.showScoresDropdown);

        setTimeout(() => {
            this.changesHistoryService.setModule(module);
            this.selectedModule = this.changesHistoryService.getModule();

            if (shouldSetParams) {
                this.setParams();
            }
            this.moduleTranslation = this.changesHistoryService.getModuleTranslation();
            this.removeFocusFromSelect();
        });
    }

    setChartType () {
        this.removeFocusFromSelect();
        this.setParams();
    }

    setDate (date: any) {
        this.updateDate(date);
        this.getCrawlHistory();
    }

    // PROTECTED

    loadLocations () {
        const params = this.state.params;
        if (params.from != null) {
            this.date.startDate = moment(params.from, this.dateFormat);
        }

        if (params.to != null) {
            this.date.endDate = moment(params.to, this.dateFormat);
        }

        if (params.module != null) {
            this.selectedModule = params.module;
            this.showScoresDropdown = this.getShowScores(this.selectedModule);
        }

        if (params.chart != null) {
            const knownChart = this.chartTypesOptions.some((chartOptions) => {
                return chartOptions.value === params.chart;
            });

            if (knownChart) {
                this.selectedChart = params.chart;
            }
        }

        this.updateDate(this.date);
    }

    getCrawlHistory () {
        if (!this.domain) {
            return;
        }

        this.setParams();
        const params = {
            from: this.date.startDate.format(this.dateFormat),
            to: this.date.endDate.format(this.dateFormat),
            domain_id: this.domain.id,
            group_id: this.domainGroup != null ? this.domainGroup.id : undefined,
            module: this.selectedModule,
        };

        this.loading = true;
        this.progress = this.crawlsRepoService.getAll(params)
            .then((history: any) => {
                this.history = history;
                this.changesHistoryService.setHistory(history.reverse());
                this.loading = false;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    setParams () {
        const params: any = {
            from: this.date.startDate.format(this.dateFormat),
            to: this.date.endDate.format(this.dateFormat),
            domain_id: this.domain.id,
            group_id: this.domainGroup !== null ? this.domainGroup.id : undefined,
            module: this.selectedModule,
        };

        if (this.showScoresDropdown) {
            params.chart = this.selectedChart;
        } else {
            params.chart = null;
        }

        this.paramService.setParams(params);
    }

    updateDate (date: any) {
        if (this.date === date) {
            return;
        }

        this.date = date;
        this.changesHistoryService.setDate(date);
        this.datePickerLoading = true;
        setTimeout(() => {
            this.datePickerLoading = false;
        });
    }

    setDomain (domain: any, domainGroup: any) {
        this.domain = domain;
        this.domainGroup = domainGroup;
        this.changesHistoryService.setDomain(domain, domainGroup);
        const modules = this.changesHistoryService.getModules();

        if (!this.isSelectedModuleAvailable(this.selectedModule, modules)) {
            this.selectedModule = 'all';
        }

        this.modules = modules;
        this.showScoresDropdown = this.getShowScores(this.selectedModule);
        this.selectedChart = this.getSelectedChart(this.showScoresDropdown);
        this.removeFocusFromSelect();
    }

    isSelectedModuleAvailable (selectedModule: any, availableModules: any) {
        return Array.isArray(availableModules) && availableModules.some((module: any) => {
            return module.value === selectedModule;
        });
    }

    removeFocusFromSelect () {
        if (document.activeElement) {
            (document.activeElement as HTMLElement).blur();
        }
    }

    getSelectedChart (showScoresDropdown: any) {
        if (showScoresDropdown) {
            return this.selectedChart || this.chartTypesOptions[0].value;
        } else {
            return this.chartTypesOptions[0].value;
        }
    }

    getShowScores (selectedModule: any) {
        return (
            this.activeFeatureService.isFeatureActive('monsido_score') &&
            this.domain != null &&
            ['seo', 'qa', 'accessibility'].indexOf(selectedModule) > -1
        );
    }
}
